/**
 * EmptyState — "there is nothing here yet".
 *
 * The portal had three of these: `.admin-empty-state` (a centred icon over a
 * line of text, on no surface), `.evaluation-empty` and `.no-results` (the same
 * declarations twice, on a surface with a 1rem radius). Which one a screen used
 * depended only on which area it belonged to, so the same "no results" message
 * looked like a panel in the moderator and like bare text in admin.
 *
 * `inline` is for an empty state inside a panel or dialog rather than one
 * filling a page — the page padding is several times too deep there, which is
 * what five call sites had each been overriding by hand.
 */

import type { ReactNode } from 'react';
import styles from './empty-state.module.css';

interface EmptyStateProps {
  /** A single emoji or glyph above the message. */
  icon?: ReactNode;
  /** Paint the surface behind it — for an empty state standing alone in the
   *  content column, where bare centred text reads as a loading failure. */
  surface?: boolean;
  /** Compact padding, for use inside a panel or dialog. */
  inline?: boolean;
  className?: string;
  children: ReactNode;
}

export function EmptyState({ icon, surface, inline, className, children }: EmptyStateProps) {
  const composed = [
    styles.base,
    surface ? styles.surface : null,
    inline ? styles.inline : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={composed}>
      {icon && (
        <div className={styles.icon} aria-hidden="true">
          {icon}
        </div>
      )}
      <div className={styles.text}>{children}</div>
    </div>
  );
}
